import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSliders } from '@fortawesome/sharp-solid-svg-icons/faSliders';
import { faGear } from '@fortawesome/pro-solid-svg-icons/faGear';

import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { Button } from 'ui-components/components/button';
import { FlexRow } from 'ui-components/components/flex';
import { Dropdown } from 'ui-components/components/INTERNAL/dropdown';
import { Text, TextType } from 'ui-components/components/text';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';

import componentMessages from './messages';
import style from './style.module.less';
import { messages } from '../../../components/orders-grid/columns/messages';
import { useEnableMultiPackageShipmentsOrderDetails } from '../../../feature-flags/hooks/use-multi-package-shipping-order-details';
import { ACCESS_LEVELS, useRequiresRateShopperAccessLevel } from '../../../hooks/use-has-rate-shopper-access-level';
import { pages } from '../../../pages/settings/nav-items';
import { setShippingDropdownPreference } from '../../../store/csw/csw.actions';
import Global from '../../../store/global-selectors';

const serviceCollectionsPath = `/${pages.settings.location}/${pages.serviceCollections.location}`;

/**
 * @param {object} props
 * @param {boolean} [props.shouldUseNewStyle]
 * @returns {React.JSX.Element}
 */
export const ServiceGroupSelector = ({ shouldUseNewStyle = false }) => {
  const selectedGrouping =
    useSelector((state) =>
      Global.selectUserUserSetting(state, {
        userSetting: 'ShippingServiceGrouping',
      }),
    ) || 'Provider';

  const dispatch = useDispatch();
  const setCarrier = useCallback(() => dispatch(setShippingDropdownPreference('Carrier')), [dispatch]);
  const setProvider = useCallback(() => dispatch(setShippingDropdownPreference('Provider')), [dispatch]);

  const showRateShopper = useRequiresRateShopperAccessLevel(ACCESS_LEVELS.FREEMIUM);
  const enableMultiPackageShipmentsOrderDetails = useEnableMultiPackageShipmentsOrderDetails();

  if (shouldUseNewStyle || enableMultiPackageShipmentsOrderDetails) {
    return (
      <Dropdown className={style['service-group-selector-dropdown']} menuPosition='bottom-right'>
        <Button className={style['service-group-selector-dropdown-button']} theme='ghost'>
          <FlexRow className={style['service-group-selector-dropdown-button-content']}>
            <FontAwesomeIcon className={style['service-group-selector-dropdown-button-icon']} icon={faSliders} />
            <Text textType={TextType.Caption} value={messages.sortBy} />
          </FlexRow>
        </Button>
        <div>
          <div className={style['options-content']}>
            <div className={style['options-title']}>
              <FormattedMessage {...messages.selectServiceBy} />
            </div>
            <div className={style.options}>
              <div className={style.option} onClick={setCarrier}>
                <input checked={selectedGrouping === 'Carrier'} id='carrier' name='carrier' type='radio' value='Carrier' />
                <label htmlFor='carrier'>
                  <FormattedMessage {...messages.selectByCarrier} />
                </label>
              </div>
              <div className={style.option} onClick={setProvider}>
                <input checked={selectedGrouping === 'Provider'} id='provider' name='provider' type='radio' value='Provider' />
                <label htmlFor='provider'>
                  <FormattedMessage {...messages.selectByPostageProvider} />
                </label>
              </div>
            </div>
            {showRateShopper ? (
              <div className={style.collections}>
                <div className={style['service-collections-title']}>
                  <FormattedMessage {...componentMessages.serviceCollectionsTitle} />
                </div>
                <div className={style['service-collections-text']}>
                  <FormattedMessage {...componentMessages.serviceCollectionsMessage} />
                </div>
                <FormattedMessage
                  {...componentMessages.serviceCollectionsLink}
                  values={{
                    a: (chunks) => <Link to={serviceCollectionsPath}>{chunks}</Link>,
                  }}
                />
              </div>
            ) : null}
          </div>
        </div>
      </Dropdown>
    );
  }

  return (
    <Tooltip position='top'>
      <div>
        <Dropdown className={style['service-group-selector-dropdown']} menuPosition='bottom-right'>
          <Button className={style['service-group-selector']} theme='unstyled'>
            <FontAwesomeIcon className={style.icon} icon={faGear} />
          </Button>
          <div>
            <div className={style['options-content']}>
              <div className={style['options-title']}>
                <FormattedMessage {...messages.selectServiceBy} />
              </div>
              <div className={style.options}>
                <div className={style.option} onClick={setCarrier}>
                  <input checked={selectedGrouping === 'Carrier'} id='carrier' name='carrier' type='radio' value='Carrier' />
                  <label htmlFor='carrier'>
                    <FormattedMessage {...messages.selectByCarrier} />
                  </label>
                </div>
                <div className={style.option} onClick={setProvider}>
                  <input checked={selectedGrouping === 'Provider'} id='provider' name='provider' type='radio' value='Provider' />
                  <label htmlFor='provider'>
                    <FormattedMessage {...messages.selectByPostageProvider} />
                  </label>
                </div>
              </div>
              {showRateShopper ? (
                <div className={style.collections}>
                  <div className={style['service-collections-title']}>
                    <FormattedMessage {...componentMessages.serviceCollectionsTitle} />
                  </div>
                  <div className={style['service-collections-text']}>
                    <FormattedMessage {...componentMessages.serviceCollectionsMessage} />
                  </div>
                  <FormattedMessage
                    {...componentMessages.serviceCollectionsLink}
                    values={{
                      a: (chunks) => <Link to={serviceCollectionsPath}>{chunks}</Link>,
                    }}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </Dropdown>
      </div>
      <TooltipContent>
        <FormattedMessage {...messages.sortBy} />
      </TooltipContent>
    </Tooltip>
  );
};
